import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/material";
import {
  type CreateContentReportError,
  type CreateContentReportPayload,
  useCreateContentReportMutation,
} from "Api/Mutations/Support/useCreateContentReportMutation";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { useContentReportCategoryCodeList } from "Components/Support/ContentReport/contentReportCategories";
import {
  CONTENT_REPORT_EXPLANATION_MAX_LENGTH,
  type ContentReportFormData,
  getContentReportSchema,
} from "Components/Support/ContentReport/contentReportSchema";
import type { ContentReportTarget } from "Components/Support/ContentReport/ContentReportTarget";
import { ContentReportTargetDescription } from "Components/Support/ContentReport/ContentReportTargetDescription";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import { CONTENT_REPORT_FORM_ID } from "Components/Support/supportFormIds";
import type { SupportFormStatus } from "Components/Support/SupportFormStatus";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  ContentReportCategory,
  ContentReportType,
} from "Infrastructure/Api/Api";
import { getReportableUrl } from "Infrastructure/Utils/SupportUtils";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const RESOURCES = Resources.Support.ContentReport;

// No category: the dropdown starts on its placeholder and zod asks for one on submit.
const DEFAULT_VALUES: Partial<ContentReportFormData> = {
  handle: "",
  explanation: "",
  name: "",
  email: "",
  isBonaFideConfirmed: false,
};

const buildPayload = (
  target: ContentReportTarget,
  values: ContentReportFormData,
  isAnonymous: boolean,
): CreateContentReportPayload => {
  const common = {
    category: values.category,
    explanation: values.explanation,
    isBonaFideConfirmed: values.isBonaFideConfirmed,
    // Only an anonymous reporter identifies themselves through the form; the server takes a
    // signed-in reporter from the token and ignores these.
    name: isAnonymous ? values.name || null : null,
    email: isAnonymous ? values.email || null : null,
  };

  if (target.kind === "message") {
    const { channelId, messageId } = target;

    return {
      ...common,
      contentType: ContentReportType.ChatMessage,
      channelId,
      messageId,
      contentUrl: getReportableUrl(),
    };
  }

  if (target.kind === "profile") {
    const { handle, identityUserPublicID } = target;

    return {
      ...common,
      contentType: ContentReportType.Profile,
      profileIdentityUserPublicID: identityUserPublicID,
      contentUrl: `${window.location.origin}${appUrl("public-profile")}/${handle ?? identityUserPublicID}`,
    };
  }

  const handle = values.handle.replace(/^@/, "");

  return {
    ...common,
    contentType: ContentReportType.Profile,
    profileHandle: handle,
    contentUrl: `${window.location.origin}${appUrl("public-profile")}/${handle}`,
  };
};

/**
 * 429 and 404/self-report get their own wording; 429 and the server-side self-report are not
 * fixable by pressing send again, a wrong handle is.
 */
const toErrorResult = (
  error: CreateContentReportError,
  t: TFunction,
): SupportFlowResult => {
  if (error.status === 429) {
    return {
      variant: "error",
      message: t(RESOURCES.RateLimitedMessage),
      canRetry: false,
    };
  }

  if (error.status === 404) {
    return {
      variant: "error",
      message: t(RESOURCES.NotFoundMessage),
      canRetry: true,
    };
  }

  const isSelfReport =
    error.status === 400 &&
    Object.values(error.data?.errors ?? {}).some((details) =>
      details?.some((detail) => detail.message === "selfReport"),
    );

  if (isSelfReport) {
    return {
      variant: "error",
      message: t(RESOURCES.SelfReportMessage),
      canRetry: false,
    };
  }

  return {
    variant: "error",
    message: t(RESOURCES.ErrorMessage),
    canRetry: true,
  };
};

type Props = {
  target: ContentReportTarget;
  /** Set when returning here from a failed send, so the retry starts from what was
      already typed instead of a blank form. */
  defaultValues?: Partial<ContentReportFormData>;
  onStatusChange: (status: SupportFormStatus) => void;
  onSubmitted: (
    result: SupportFlowResult,
    submittedValues: ContentReportFormData,
  ) => void;
};

/**
 * The DSA Art. 16 notice form (ZS-1743). Same shape as SupportRequestFormStep — the submit
 * button lives in the host's footer and reaches the form by id — with the four elements the
 * regulation asks for: the location (the target, or a handle), a legal category plus a
 * substantiated explanation, the reporter's contact, and the bona-fide statement.
 */
export const ContentReportFormStep = ({
  target,
  defaultValues,
  onStatusChange,
  onSubmitted,
}: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const isAnonymous = !user;
  const isHandleRequired = target.kind === "profileByHandle";
  const categories = useContentReportCategoryCodeList();

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<ContentReportFormData>({
    resolver: zodResolver(
      getContentReportSchema(t, { isAnonymous, isHandleRequired }),
    ),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const { mutate: createContentReport, isPending } =
    useCreateContentReportMutation({
      onSuccess: ({ referenceNumber }) => {
        onSubmitted(
          {
            variant: "success",
            message: referenceNumber
              ? t(RESOURCES.SuccessMessageWithReference, { referenceNumber })
              : t(RESOURCES.SuccessMessage),
            canRetry: false,
          },
          getValues(),
        );
      },
      onError: (error) => onSubmitted(toErrorResult(error, t), getValues()),
    });

  // React Hook Form and the mutation expose these only as derived render state, so lifting
  // them to the host — which owns the footer buttons and the discard-changes guard — takes an
  // effect.
  useEffect(() => {
    onStatusChange({ isDirty, isBusy: isPending });
  }, [isDirty, isPending, onStatusChange]);

  const onSubmit = (values: ContentReportFormData) =>
    createContentReport(buildPayload(target, values, isAnonymous));

  const explanationLength = watch("explanation")?.length ?? 0;
  const isContactOptional =
    isAnonymous && watch("category") === ContentReportCategory.ChildSexualAbuse;

  return (
    <form id={CONTENT_REPORT_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        <ContentReportTargetDescription target={target} />
        {isHandleRequired && (
          <FormInput
            control={control}
            name="handle"
            label={t(RESOURCES.HandleLabel)}
            placeholder={t(RESOURCES.HandlePlaceholder)}
            formControlClass="fullWidth"
            fullWidth
          />
        )}
        <Dropdown
          control={control}
          name="category"
          codeList={categories}
          label={t(RESOURCES.CategoryLabel)}
          placeholder={t(RESOURCES.CategoryPlaceholder)}
        />
        <FormInput
          control={control}
          name="explanation"
          label={t(RESOURCES.ExplanationLabel)}
          multiline
          rows={6}
          maxLength={CONTENT_REPORT_EXPLANATION_MAX_LENGTH}
          formControlClass="fullWidth"
          fullWidth
          helperText={
            <Box
              display="flex"
              justifyContent="space-between"
              gap={2}
              component="span"
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {t(RESOURCES.ExplanationHelper)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                flexShrink={0}
              >
                {t(Resources.Support.Request.DescriptionCharCount, {
                  count: explanationLength,
                  maxCount: CONTENT_REPORT_EXPLANATION_MAX_LENGTH,
                })}
              </Typography>
            </Box>
          }
        />
        {isAnonymous && (
          <>
            <FormInput
              control={control}
              name="name"
              label={t(RESOURCES.NameLabel)}
              formControlClass="fullWidth"
              fullWidth
            />
            <FormInput
              control={control}
              name="email"
              type="email"
              label={t(RESOURCES.EmailLabel)}
              formControlClass="fullWidth"
              fullWidth
            />
            {isContactOptional && (
              <Typography variant="body2" color="text.secondary">
                {t(RESOURCES.ContactOptionalNote)}
              </Typography>
            )}
          </>
        )}
        <CheckboxInput
          control={control}
          name="isBonaFideConfirmed"
          label={t(RESOURCES.BonaFideLabel)}
        />
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.LegalNote)}
        </Typography>
      </Stack>
    </form>
  );
};
